// Utility functions for time formatting

/**
 * @typedef {Object} TimeSlot
 * @property {string} start - Start time in 12-hour format
 * @property {string} end - End time in 12-hour format
 */

/**
 * Convert 24-hour time (HH:MM) to 12-hour format (HH:MM AM/PM)
 * @param {string} time24 - Time in 24-hour format (e.g., "14:30")
 * @returns {string} Time in 12-hour format (e.g., "2:30 PM")
 */
export const formatTo12Hour = (time24) => {
  if (!time24) return '';
  
  // Handle if already in 12-hour format
  if (time24.includes('AM') || time24.includes('PM')) {
    return time24;
  }
  
  const [hours, minutes] = time24.split(':');
  const hour24 = parseInt(hours, 10);
  const mins = minutes || '00';
  
  // Validate input
  if (isNaN(hour24) || hour24 < 0 || hour24 > 23) {
    console.warn('Invalid hour in formatTo12Hour:', time24, hour24);
    return time24; // Return as-is if invalid
  }
  
  if (hour24 === 0) {
    return `12:${mins} AM`;
  } else if (hour24 < 12) {
    return `${hour24}:${mins} AM`;
  } else if (hour24 === 12) {
    return `12:${mins} PM`;
  } else {
    return `${hour24 - 12}:${mins} PM`;
  }
};

/**
 * Convert 12-hour time (HH:MM AM/PM) to 24-hour format (HH:MM)
 * @param {string} time12 - Time in 12-hour format (e.g., "2:30 PM")
 * @returns {string} Time in 24-hour format (e.g., "14:30")
 */
export const formatTo24Hour = (time12) => {
  if (!time12) return '';
  
  const [timePart, period] = time12.trim().split(' ');
  const [hours, minutes] = timePart.split(':');
  let hour24 = parseInt(hours, 10);
  const mins = minutes || '00';
  
  if (period === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (period === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  
  return `${hour24.toString().padStart(2, '0')}:${mins}`;
};

/**
 * Format time slot for display (e.g., "9:00 AM - 11:00 AM")
 * @param {string} startTime - Start time (can be 12-hour or 24-hour format)
 * @param {string} endTime - End time (can be 12-hour or 24-hour format)
 * @returns {string} Formatted time slot
 */
export const formatTimeSlot = (startTime, endTime) => {
  if (!startTime || !endTime) {
    console.warn('formatTimeSlot: Missing times', { startTime, endTime });
    return '';
  }
  
  // Check if times are already in 12-hour format (contain AM/PM)
  const isStart12Hour = startTime.includes('AM') || startTime.includes('PM') || startTime.includes('am') || startTime.includes('pm');
  const isEnd12Hour = endTime.includes('AM') || endTime.includes('PM') || endTime.includes('am') || endTime.includes('pm');
  
  // If both are already in 12-hour format, just combine them (ensure proper formatting)
  if (isStart12Hour && isEnd12Hour) {
    // Normalize case for consistency
    const startFormatted = startTime.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
    const endFormatted = endTime.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
    return `${startFormatted} - ${endFormatted}`;
  }
  
  // Otherwise, convert to 12-hour format if needed
  let start12, end12;
  try {
    start12 = isStart12Hour ? startTime : formatTo12Hour(startTime);
    end12 = isEnd12Hour ? endTime : formatTo12Hour(endTime);
  } catch (e) {
    console.error('Error in formatTimeSlot conversion:', e, { startTime, endTime });
    return `${startTime} - ${endTime}`; // Fallback to original
  }
  
  // Validate that conversion worked
  if (!start12 || !end12 || start12.includes('NaN') || end12.includes('NaN')) {
    console.warn('formatTimeSlot: Invalid conversion result', { startTime, endTime, start12, end12 });
    return `${startTime} - ${endTime}`; // Fallback to original
  }
  
  // Ensure both have AM/PM
  const startFinal = start12.includes('AM') || start12.includes('PM') ? start12 : formatTo12Hour(startTime);
  const endFinal = end12.includes('AM') || end12.includes('PM') ? end12 : formatTo12Hour(endTime);
  
  // Final validation
  if (startFinal.includes('NaN') || endFinal.includes('NaN')) {
    console.warn('formatTimeSlot: NaN in final result', { startTime, endTime, startFinal, endFinal });
    return `${startTime} - ${endTime}`; // Fallback to original
  }
  
  return `${startFinal} - ${endFinal}`;
};

/**
 * Parse time from slot string (e.g., "9:00 AM - 11:00 AM")
 * @param {string} timeSlot - Time slot string
 * @returns {TimeSlot} Object with start and end times in 12-hour format
 */
export const parseTimeFromSlot = (timeSlot) => {
  if (!timeSlot) return { start: '', end: '' };
  
  const [startTime, endTime] = timeSlot.split(' - ');
  return {
    start: startTime?.trim() || '',
    end: endTime?.trim() || ''
  };
};

/**
 * Convert time to minutes for comparison
 * @param {string} time12 - Time in 12-hour format (e.g., "2:30 PM")
 * @returns {number} Time in minutes from midnight
 */
export const timeToMinutes = (time12) => {
  if (!time12) return 0;
  
  const [timePart, period] = time12.trim().split(' ');
  const [hours, minutes] = timePart.split(':');
  let hour24 = parseInt(hours, 10);
  const mins = parseInt(minutes || '0', 10);
  
  if (period === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (period === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  
  return hour24 * 60 + mins;
};

/**
 * Get user's timezone
 * @returns {string} Timezone string (e.g., "Asia/Singapore")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Convert UTC datetime string to local time string for display
 * @param {string} utcDateTime - UTC datetime string (ISO format)
 * @param {string} format - Format: '12hour' | '24hour' | 'datetime'
 * @returns {string} Local time string
 */
export const utcToLocalTime = (utcDateTime, format = '12hour') => {
  if (!utcDateTime) return '';
  
  const date = new Date(utcDateTime);
  const timezone = getUserTimezone();
  
  if (format === 'datetime') {
    return date.toLocaleString('en-US', { timeZone: timezone });
  }
  
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const mins = minutes.toString().padStart(2, '0');
  
  if (format === '24hour') {
    return `${hours.toString().padStart(2, '0')}:${mins}`;
  }
  
  // 12-hour format
  let hour12 = hours % 12;
  if (hour12 === 0) hour12 = 12;
  const period = hours < 12 ? 'AM' : 'PM';
  return `${hour12}:${mins} ${period}`;
};

/**
 * Convert local time string to UTC datetime
 * @param {string} date - Date string (YYYY-MM-DD)
 * @param {string} localTime - Local time string (12-hour or 24-hour format)
 * @returns {string} UTC datetime string (ISO format)
 */
export const localTimeToUTC = (date, localTime) => {
  if (!date || !localTime) return '';
  
  // Parse local time to 24-hour format
  let hour24, minutes;
  if (localTime.includes('AM') || localTime.includes('PM')) {
    const [timePart, period] = localTime.trim().split(' ');
    const [hours, mins] = timePart.split(':');
    hour24 = parseInt(hours, 10);
    minutes = parseInt(mins || '0', 10);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
  } else {
    const [hours, mins] = localTime.split(':');
    hour24 = parseInt(hours, 10);
    minutes = parseInt(mins || '0', 10);
  }
  
  // Create date string (this will be interpreted as local time)
  const localDateStr = `${date}T${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  const localDate = new Date(localDateStr);
  
  // Get UTC equivalent by accounting for timezone offset
  const offset = localDate.getTimezoneOffset(); // offset in minutes
  const utcTime = new Date(localDate.getTime() - (offset * 60000));
  
  return utcTime.toISOString();
};

/**
 * Convert UTC time-of-day to local time-of-day (for availability display)
 * Uses a reference date to account for DST properly
 * @param {string} utcTime - UTC time string (HH:MM format, 24-hour)
 * @param {string} format - Format: '12hour' | '24hour'
 * @param {Date} referenceDate - Optional reference date for DST calculation (defaults to today)
 * @returns {string} Local time string
 */
export const utcTimeOfDayToLocal = (utcTime, format = '12hour', referenceDate = new Date()) => {
  if (!utcTime) return '';
  
  // Check if already in 12-hour format (old data or already converted)
  if (utcTime.includes('AM') || utcTime.includes('PM') || utcTime.includes('am') || utcTime.includes('pm')) {
    return utcTime; // Return as-is if already in local format
  }
  
  // Parse UTC time (should be in 24-hour format like "09:00")
  const timeMatch = utcTime.match(/^(\d{1,2}):(\d{2})$/);
  if (!timeMatch) {
    console.warn('Invalid UTC time format:', utcTime);
    return utcTime; // Return as-is if invalid
  }
  
  const utcHours = parseInt(timeMatch[1], 10);
  const utcMinutes = parseInt(timeMatch[2], 10);
  
  if (isNaN(utcHours) || isNaN(utcMinutes) || utcHours < 0 || utcHours > 23 || utcMinutes < 0 || utcMinutes > 59) {
    console.warn('Invalid UTC time values:', utcTime, { utcHours, utcMinutes });
    return utcTime; // Return as-is if invalid
  }
  
  // Create a date object for today in local timezone
  let today;
  if (referenceDate instanceof Date) {
    today = referenceDate;
  } else if (referenceDate) {
    today = new Date(referenceDate);
  } else {
    today = new Date();
  }
  
  // Validate the date
  if (isNaN(today.getTime())) {
    console.warn('Invalid referenceDate in utcTimeOfDayToLocal:', referenceDate);
    today = new Date();
  }
  
  const year = today.getFullYear();
  const month = today.getMonth();
  const date = today.getDate();
  
  // Validate date components
  if (isNaN(year) || isNaN(month) || isNaN(date)) {
    console.warn('Invalid date components in utcTimeOfDayToLocal:', { year, month, date, referenceDate });
    return utcTime; // Return original if date is invalid
  }
  
  // Create UTC date/time (month is 0-indexed in Date.UTC, so no adjustment needed)
  const utcDate = new Date(Date.UTC(year, month, date, utcHours, utcMinutes));
  
  // Validate the UTC date
  if (isNaN(utcDate.getTime())) {
    console.warn('Invalid UTC date created in utcTimeOfDayToLocal:', { year, month, date, utcHours, utcMinutes });
    return utcTime; // Return original if date creation failed
  }
  
  // Get local time components (this automatically accounts for timezone offset)
  const localHours = utcDate.getHours();
  const localMinutes = utcDate.getMinutes();
  
  // Validate local time components
  if (isNaN(localHours) || isNaN(localMinutes)) {
    console.warn('Invalid local time components in utcTimeOfDayToLocal:', { localHours, localMinutes, utcDate });
    return utcTime; // Return original if conversion failed
  }
  
  const mins = localMinutes.toString().padStart(2, '0');
  
  if (format === '24hour') {
    return `${localHours.toString().padStart(2, '0')}:${mins}`;
  }
  
  // 12-hour format with AM/PM
  let hour12 = localHours % 12;
  if (hour12 === 0) hour12 = 12;
  const period = localHours < 12 ? 'AM' : 'PM';
  
  // Validate result
  if (isNaN(hour12) || isNaN(localMinutes)) {
    console.warn('utcTimeOfDayToLocal: NaN in conversion result', { utcTime, localHours, localMinutes, hour12 });
    return utcTime; // Return original if conversion failed
  }
  
  return `${hour12}:${mins} ${period}`;
};

/**
 * Convert local time-of-day to UTC time-of-day (for availability storage)
 * Uses a reference date to account for DST properly
 * @param {string} localTime - Local time string (12-hour or 24-hour format)
 * @param {string} format - Output format: '12hour' | '24hour'
 * @param {Date} referenceDate - Optional reference date for DST calculation (defaults to today)
 * @returns {string} UTC time string (24-hour format)
 */
export const localTimeOfDayToUTC = (localTime, format = '24hour', referenceDate = new Date()) => {
  if (!localTime) return '';
  
  // Parse local time to 24-hour format
  let hour24, minutes;
  if (localTime.includes('AM') || localTime.includes('PM')) {
    const [timePart, period] = localTime.trim().split(' ');
    const [hours, mins] = timePart.split(':');
    hour24 = parseInt(hours, 10);
    minutes = parseInt(mins || '0', 10);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
  } else {
    const [hours, mins] = localTime.split(':');
    hour24 = parseInt(hours, 10);
    minutes = parseInt(mins || '0', 10);
  }
  
  // Create local date with reference date
  const localDate = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
    hour24,
    minutes
  );
  
  // Get UTC equivalent
  const utcHours = localDate.getUTCHours();
  const utcMinutes = localDate.getUTCMinutes();
  const utcMins = utcMinutes.toString().padStart(2, '0');
  
  if (format === '24hour') {
    return `${utcHours.toString().padStart(2, '0')}:${utcMins}`;
  }
  
  // 12-hour format
  let hour12 = utcHours % 12;
  if (hour12 === 0) hour12 = 12;
  const period = utcHours < 12 ? 'AM' : 'PM';
  return `${hour12}:${utcMins} ${period}`;
};